import {yupResolver} from "@hookform/resolvers/yup";
import {useEffect, useMemo} from "react";
import {FormProvider, useForm} from "react-hook-form";

import {ColumnSelectorField} from "@/components/form/fields/ColumnSelectorField/ColumnSelectorField.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {SelectField} from "@/components/form/fields/SelectField/SelectField.tsx";
import {SwitchField} from "@/components/form/fields/SwitchField.tsx";
import {SelectOption} from "@/components/select/Select.types.ts";
import {ChecksListColumnDef, ChecksListView} from "@/lib/types/checksListViews.ts";
import {ResponseStatus} from "@/lib/types/common.ts";
import {useChecksListViewsContext} from "@/pages/checks/list/columns/ChecksListViewsContext.tsx";
import {ViewEditorFormFields} from "@/pages/checks/list/components/ViewEditor/ViewEditor.types.ts";
import {
  buildDefaultValues,
  sortDirectionOptions,
  viewEditorSchema,
} from "@/pages/checks/list/components/ViewEditor/ViewEditorForm.utils.ts";

import styles from "./ViewEditorForm.module.scss";

interface ViewEditorFormProps {
  formId: string;
  onSave: () => void;
  onSubmittingChange: (submitting: boolean) => void;
  availableColumns: ChecksListColumnDef[];
  editingView: ChecksListView | null;
}

export const ViewEditorForm = ({
  formId,
  onSave,
  onSubmittingChange,
  availableColumns,
  editingView,
}: ViewEditorFormProps) => {
  const {saveView} = useChecksListViewsContext();

  const form = useForm<ViewEditorFormFields>({
    resolver: yupResolver(viewEditorSchema),
    defaultValues: buildDefaultValues(editingView, availableColumns),
  });

  const {
    watch,
    setValue,
    setError,
    handleSubmit,
    formState: {isSubmitting},
  } = form;

  const selectedColumns = watch("columns");
  const defaultSort = watch("default_sort");

  const sortColumnOptions: SelectOption[] = useMemo(() => {
    return selectedColumns
      .filter((col) => col.sortable)
      .map((col) => ({label: col.label, value: col.sort_field ?? col.id}));
  }, [selectedColumns]);

  useEffect(() => {
    const validSortFields = sortColumnOptions.map((option) => option.value);
    if (validSortFields.length > 0 && !validSortFields.includes(defaultSort)) {
      setValue("default_sort", validSortFields[0]);
    }
  }, [sortColumnOptions, defaultSort, setValue]);

  // Surface the form's submission state so the modal footer can disable its Save button.
  useEffect(() => {
    onSubmittingChange(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  useEffect(() => () => onSubmittingChange(false), [onSubmittingChange]);

  const submit = async (data: ViewEditorFormFields) => {
    const result = await saveView(editingView?.id ?? null, {
      ...data,
      columns: data.columns.map((col) => col.id),
    });
    if (result?.status === ResponseStatus.ERROR) {
      const nameError = result.message.name?.[0];
      if (nameError) {
        setError("name", {message: nameError});
      }
      return;
    }
    onSave();
  };

  return (
    <FormProvider {...form}>
      <form id={formId} onSubmit={handleSubmit(submit)} className={"d-flex flex-column gap-4"}>
        <InputField name="name" label="View Name" placeholder="e.g. HTTP Checks Overview" required maxLength={100} />

        <ColumnSelectorField name="columns" availableColumns={availableColumns} />

        <div className={styles.sortRow}>
          <div className={styles.sortColumn}>
            <SelectField
              name="default_sort"
              label="Default Sort Column"
              options={sortColumnOptions}
              disabled={sortColumnOptions.length === 0}
              placeholder="No sortable columns selected"
            />
          </div>
          <div className={styles.sortDirection}>
            <SelectField name="default_sort_direction" label="Direction" options={sortDirectionOptions} />
          </div>
          <div className={styles.sortDefault}>
            <SwitchField name="is_default" label="Set as default" />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
